/**
 * Keyset "load more" without a state library.
 *
 * A fetcher-only route reuses the list's page loader; `fetcher.load()` reads it
 * without navigating, and the rows append to what the route loader already
 * returned. Accumulated rows reset whenever the query changes, since they belong
 * to a filtered set that no longer applies.
 *
 * Each list route exports a `pageLoader` returning *only* the page — a route
 * loader that also returns lookups or a registry would hand the fetcher the
 * wrong shape.
 */

import { useEffect, useState } from 'react'
import { useFetcher, useSearchParams } from 'react-router-dom'

export interface PageOf<T> {
  rows: T[]
  nextCursor: string | null
  totalCount: number
}

export interface Paged<T> {
  rows: T[]
  cursor: string | null
  loading: boolean
  loadMore: () => void
}

export function usePagedRows<T extends { id: string }>(
  page: PageOf<T>,
  pagePath: string,
): Paged<T> {
  const [searchParams] = useSearchParams()
  const fetcher = useFetcher<PageOf<T>>()

  const [extra, setExtra] = useState<T[]>([])
  const [cursor, setCursor] = useState<string | null>(page.nextCursor)

  const queryKey = searchParams.toString()
  useEffect(() => {
    setExtra([])
    setCursor(page.nextCursor)
  }, [queryKey, page.nextCursor])

  const fetched = fetcher.data
  useEffect(() => {
    if (!fetched) return
    setExtra((rows) => [...rows, ...fetched.rows])
    setCursor(fetched.nextCursor)
  }, [fetched])

  return {
    rows: [...page.rows, ...extra],
    cursor,
    loading: fetcher.state !== 'idle',
    loadMore: () => {
      if (!cursor) return
      const params = new URLSearchParams(searchParams)
      params.set('cursor', cursor)
      fetcher.load(`${pagePath}?${params.toString()}`)
    },
  }
}
